import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useAuth } from '../context/AuthContext';

// Toast Notification Component
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 3000 }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-blue-500'
  }[type];

  const icon = {
    success: '✓',
    error: '✗',
    info: 'ℹ'
  }[type];

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 min-w-80`}>
        <span className="text-xl">{icon}</span>
        <span className="flex-1">{message}</span>
        <button onClick={onClose} className="text-white hover:text-gray-200 text-lg">
          &times;
        </button>
      </div>
    </div>
  );
};

// Instruction Question Interface - Updated to match your backend schema
interface InstructionQuestion {
  id: number;
  question: string;
  order: number;
  is_active: boolean;
  created_at?: string;
}

const ProcessDefinitionsInstructions: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  
  // Instruction Questions State
  const [questions, setQuestions] = useState<InstructionQuestion[]>([]);
  
  // New question state
  const [newQuestion, setNewQuestion] = useState('');
  
  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  
  // Edit states
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  
  // Delete confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; question: string } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  // Fetch instruction questions from API
  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      console.log('🔍 Fetching instruction questions from:', `${API_URL}/api/instruction-questions`);
      
      const response = await fetch(`${API_URL}/api/instruction-questions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📥 Fetch response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Fetched instruction questions:', data);
      
      // Handle response format
      if (data && data.success && Array.isArray(data.data)) {
        setQuestions(data.data);
      } else {
        console.error('Unexpected response format:', data);
        showToast('Failed to load questions: Unexpected response format', 'error');
      }
      
    } catch (err: any) {
      console.error('❌ Error fetching instruction questions:', err);
      showToast(err.message || 'Failed to load questions', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Add new instruction question
  const handleAddQuestion = async () => {
    if (!newQuestion.trim()) {
      showToast('Please enter a question', 'error');
      return;
    }

    // Check for duplicates
    if (questions.some(q => q.question.toLowerCase() === newQuestion.toLowerCase().trim())) {
      showToast('This question already exists', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const newQuestionObj = {
        question: newQuestion.trim(),
        is_active: true
      };

      const response = await fetch(`${API_URL}/api/instruction-questions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newQuestionObj),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.success && data.data) {
        setQuestions(prev => [...prev, data.data]);
        showToast('Instruction question added successfully!');
        setNewQuestion('');
      } else {
        throw new Error('Unexpected response format');
      }
      
    } catch (err: any) {
      console.error('❌ Error adding instruction question:', err);
      showToast(err.message || 'Failed to add question', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Start editing
  const handleStartEdit = (question: InstructionQuestion) => {
    setEditingId(question.id);
    setEditQuestion(question.question);
  };

  // Save edits
  const handleSaveEdit = async (id: number) => {
    if (!editQuestion.trim()) {
      showToast('Question cannot be empty', 'error');
      return;
    }

    // Check for duplicates (excluding current item)
    if (questions.some(q => q.id !== id && q.question.toLowerCase() === editQuestion.toLowerCase().trim())) {
      showToast('Another question with this text already exists', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const updatedQuestion = {
        question: editQuestion.trim()
      };

      const response = await fetch(`${API_URL}/api/instruction-questions/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedQuestion),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Update local state
      setQuestions(prev => prev.map(q => 
        q.id === id ? { 
          ...q, 
          question: editQuestion.trim(),
        } : q
      ));

      setEditingId(null);
      setEditQuestion('');
      showToast('Instruction question updated successfully!');
      
    } catch (err: any) {
      console.error('Error updating question:', err);
      showToast(err.message || 'Failed to update question', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditQuestion('');
  };

  // Handle remove click
  const handleRemoveClick = (id: number, question: string) => {
    setItemToDelete({ id, question });
    setShowConfirmModal(true);
  };

  // Handle confirmed deletion
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    
    const { id } = itemToDelete;
    setShowConfirmModal(false);
    setDeletingId(id);
    
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const response = await fetch(`${API_URL}/api/instruction-questions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Remove from local state
      setQuestions(prev => prev.filter(q => q.id !== id));
      showToast('Instruction question removed successfully!');
      
    } catch (err: any) {
      console.error('Error removing question:', err);
      showToast(err.message || 'Failed to remove question', 'error');
    } finally {
      setIsLoading(false);
      setDeletingId(null);
      setItemToDelete(null);
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setItemToDelete(null);
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  return (
    <>
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}

      {/* Confirmation Modal for Delete */}
      {showConfirmModal && itemToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-70 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                Confirm Delete
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to remove this instruction question?
              </p>
              <div className="p-3 bg-gray-100 rounded mb-4">
                <p className="font-medium text-gray-900">"{itemToDelete.question}"</p>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                This action cannot be undone.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400"
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <Sidebar 
        isVisible={isSidebarVisible} 
        toggleSidebar={toggleSidebar} 
      />

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <Header 
          title="Instruction Questions"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />

        {/* Page Content */}
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <button
              onClick={() => navigate('/process-definitions')}
              className="mb-6 flex items-center text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Process Definitions
            </button>

            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Instruction Questions
              </h1>
              <p className="text-gray-600">
                Configure questions that will appear at the bottom of instruction details.
                These questions help capture additional information during the instruction process.
              </p>
            </div>

            {/* Add New Question Section */}
            <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Add New Instruction Question
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question
                  </label>
                  <textarea
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter a new instruction question (e.g., 'Has the client provided all necessary documentation?')"
                    rows={3}
                    disabled={isLoading}
                  />
                </div>
          
                <div className="flex justify-end">
                  <button
                    onClick={handleAddQuestion}
                    disabled={isLoading || !newQuestion.trim()}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Question
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Questions List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Instruction Questions
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {questions.length} question{questions.length !== 1 ? 's' : ''} configured
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    These questions will appear on instruction details
                  </div>
                </div>
              </div>

              {isLoading && questions.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-700">Loading instruction questions...</p>
                </div>
              ) : questions.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-4xl mb-4 text-gray-300">❓</div>
                  <p className="text-lg font-medium text-gray-900 mb-2">No instruction questions yet</p>
                  <p className="text-gray-600">Add your first question above to get started.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {questions.map((question) => (
                    <div key={question.id} className="p-6 hover:bg-gray-50 transition-colors">
                      {editingId === question.id ? (
                        // Edit Mode
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Question
                            </label>
                            <textarea
                              value={editQuestion}
                              onChange={(e) => setEditQuestion(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              rows={3}
                              disabled={isLoading}
                            />
                          </div>
                          
                          <div className="flex justify-end space-x-3">
                            <button
                              onClick={() => handleSaveEdit(question.id)}
                              disabled={isLoading || !editQuestion.trim()}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors flex items-center"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Save Changes
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={isLoading}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-4">
                            <p className="text-gray-900 mb-2">{question.question}</p>
                            <div className="flex items-center space-x-3">
                              
                              {question.created_at && (
                                <span className="text-xs text-gray-500">
                                  Added: {new Date(question.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleStartEdit(question)}
                              disabled={isLoading}
                              className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors flex items-center"
                              title="Edit question"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                            
                            <button
                              onClick={() => handleRemoveClick(question.id, question.question)}
                              disabled={isLoading || deletingId === question.id}
                              className="px-3 py-1.5 text-sm bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors flex items-center"
                              title="Remove question"
                            >
                              {deletingId === question.id ? (
                                <>
                                  <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-red-700" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Removing...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Remove
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Information Card */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">About Instruction Questions</h3>
                  <div className="text-blue-700 space-y-2">
                    <p className="text-sm">
                      Instruction questions appear at the bottom of each instruction detail view. They help:
                    </p>
                    <ul className="list-disc pl-5 text-sm space-y-1">
                      <li>Capture additional information during the instruction process</li>
                      <li>Ensure compliance requirements are met</li>
                      <li>Track client confirmations and acknowledgments</li>
                      <li>Standardize data collection across all instructions</li>
                    </ul>
                    <div className="mt-4 p-3 bg-white bg-opacity-50 rounded border border-blue-200">
                      <p className="text-xs text-blue-800 font-medium mb-1">Best Practices:</p>
                      <ul className="text-xs text-blue-700 list-disc pl-4">
                        <li>Keep questions clear and concise</li>
                        <li>Focus on yes/no questions for simplicity</li>
                        <li>Mark critical questions as "Required"</li>
                        <li>Review and update questions regularly based on business needs</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProcessDefinitionsInstructions;